import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { chatService } from '@/services';

/**
 * 页面类型枚举
 */
export type PageType = 'text' | 'image' | 'video';

/**
 * 会话状态枚举
 */
export type SessionState = 'IDLE' | 'LOADING' | 'ACTIVE' | 'SYNCING' | 'ERROR';

/**
 * 跨页面会话数据接口
 */
export interface CrossPageSessionData {
  sessionId: string;
  timestamp: number;
  source: PageType; // 会话来源页面
  lastActiveTime: number;
  projectId?: string; // 关联的项目ID
  sessionName?: string; // 会话名称
  projectName?: string; // 项目名称
}

/**
 * 跨页面会话状态接口
 */
export interface CrossPageSessionState {
  currentSession: CrossPageSessionData | null;
  sessionState: SessionState;
  hasRestoredSession: boolean;
  lastSyncTime: number;
  error: string | null;
}

/**
 * 设置会话参数接口
 */
export interface SetSessionParams {
  sessionId: string;
  source: PageType;
  projectId?: string;
  skipValidation?: boolean;
  sessionName?: string;
  projectName?: string;
}

/**
 * 验证会话有效性的异步thunk
 */
export const validateSession = createAsyncThunk(
  'crossPageSession/validateSession',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const response = await chatService.getChatFeedList({ chat_id: sessionId });
      return response.code === 0;
    } catch (error) {
      console.error('会话验证失败:', error);
      return rejectWithValue('会话验证失败');
    }
  }
);

/**
 * 设置会话的异步thunk
 */
export const setSession = createAsyncThunk(
  'crossPageSession/setSession',
  async (params: SetSessionParams, { dispatch, rejectWithValue }) => {
    const {
      sessionId,
      source,
      projectId,
      skipValidation = false,
      sessionName,
      projectName,
    } = params;

    try {
      // 如果不跳过验证，先验证会话有效性
      if (!skipValidation) {
        const isValid = await dispatch(validateSession(sessionId)).unwrap();
        if (!isValid) {
          return rejectWithValue('会话无效');
        }
      }

      const sessionData: CrossPageSessionData = {
        sessionId,
        source,
        projectId,
        sessionName,
        projectName,
        timestamp: Date.now(),
        lastActiveTime: Date.now(),
      };

      return sessionData;
    } catch (error) {
      return rejectWithValue('设置会话失败');
    }
  }
);

const initialState: CrossPageSessionState = {
  currentSession: null,
  sessionState: 'IDLE',
  hasRestoredSession: false,
  lastSyncTime: 0,
  error: null,
};

const crossPageSessionSlice = createSlice({
  name: 'crossPageSession',
  initialState,
  reducers: {
    // 从localStorage恢复会话
    restoreSession: (state, action: PayloadAction<CrossPageSessionData>) => {
      state.currentSession = action.payload;
      state.hasRestoredSession = true;
      state.sessionState = 'ACTIVE';
      state.lastSyncTime = Date.now();
    },

    // 更新会话活跃时间
    updateLastActiveTime: state => {
      if (state.currentSession) {
        state.currentSession.lastActiveTime = Date.now();
        state.lastSyncTime = Date.now();
      }
    },

    // 清除当前会话
    clearSession: state => {
      state.currentSession = null;
      state.sessionState = 'IDLE';
      state.hasRestoredSession = false;
      state.error = null;
    },

    // 清除错误状态
    clearError: state => {
      state.error = null;
    },

    // 设置会话状态
    setSessionState: (state, action: PayloadAction<SessionState>) => {
      state.sessionState = action.payload;
    },

    // 更新会话名称
    updateSessionName: (
      state,
      action: PayloadAction<{ sessionId: string; sessionName: string }>
    ) => {
      if (state.currentSession && state.currentSession.sessionId === action.payload.sessionId) {
        state.currentSession.sessionName = action.payload.sessionName;
        state.lastSyncTime = Date.now();
      }
    },

    // 重置状态
    resetState: () => initialState,
  },
  extraReducers: builder => {
    builder
      // 处理Redux persist的rehydrate action
      .addCase(REHYDRATE, (state, action: any) => {
        if (action.payload?.crossPageSession) {
          const persistedState = action.payload.crossPageSession;

          // 检查持久化的会话是否过期
          if (persistedState.currentSession) {
            const maxAge = 24 * 60 * 60 * 1000; // 24小时
            const isExpired = Date.now() - persistedState.currentSession.timestamp > maxAge;

            if (!isExpired) {
              // 会话未过期，恢复状态
              state.currentSession = persistedState.currentSession;
              state.hasRestoredSession = true;
              state.sessionState = 'ACTIVE';
              state.lastSyncTime = persistedState.lastSyncTime || Date.now();
            } else {
              // 会话已过期，清除状态
              state.currentSession = null;
              state.hasRestoredSession = false;
              state.sessionState = 'IDLE';
            }
          }
        }
      })

      // 验证会话
      .addCase(validateSession.pending, state => {
        state.sessionState = 'LOADING';
        state.error = null;
      })
      .addCase(validateSession.fulfilled, state => {
        state.sessionState = 'ACTIVE';
      })
      .addCase(validateSession.rejected, (state, action) => {
        state.sessionState = 'ERROR';
        state.error = action.payload as string;
      })

      // 设置会话
      .addCase(setSession.pending, state => {
        state.sessionState = 'SYNCING';
        state.error = null;
      })
      .addCase(setSession.fulfilled, (state, action) => {
        state.currentSession = action.payload;
        state.sessionState = 'ACTIVE';
        state.lastSyncTime = Date.now();
      })
      .addCase(setSession.rejected, (state, action) => {
        state.sessionState = 'ERROR';
        state.error = action.payload as string;
      });
  },
});

export const {
  restoreSession,
  updateLastActiveTime,
  clearSession,
  clearError,
  setSessionState,
  updateSessionName,
  resetState,
} = crossPageSessionSlice.actions;

export default crossPageSessionSlice.reducer;
